import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Badge, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { dejavuSans } from '../../../../../fonts/DejaVuSans-normal';
import { dejavuSansBold } from '../../../../../fonts/DejaVuSans-Bold-bold';
import Dropdown from '../../../../primer/Dropdown';
import { getCurrentUser } from "../../../../../_helpers/commonFunctions";
import axios from "axios";

const THEME_COLOR = '#76AEB7';

const checkboxBaseStyle = {
    position: 'static',
    width: '18px',
    height: '18px',
    margin: '0 10px 0 0',
    flexShrink: 0,
    cursor: 'pointer',
    appearance: 'none',
    WebkitAppearance: 'none',
    border: '1.5px solid #94a3b8',
    borderRadius: '4px',
    outline: 'none',
    transition: 'background-color 0.15s ease'
};

const getCheckboxStyle = (checked, indeterminate = false) => {
    if (indeterminate) {
        return {
            ...checkboxBaseStyle,
            backgroundColor: THEME_COLOR,
            borderColor: THEME_COLOR,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10h8'/%3E%3C/svg%3E")`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        };
    }
    if (checked) {
        return {
            ...checkboxBaseStyle,
            backgroundColor: THEME_COLOR,
            borderColor: THEME_COLOR,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3E%3Cpath fill='none' stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='M6 10l3 3l6-6'/%3E%3C/svg%3E")`,
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        };
    }
    return {
        ...checkboxBaseStyle,
        backgroundColor: '#ffffff'
    };
};

const formatCurrency = (val) => {
    return Number(val || 0).toLocaleString('el-GR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const resolvePaymentIdentity = (debt) => {
    const rawCode = debt?.paymentCode || debt?.to || '';
    const cleanCode = String(rawCode).trim();
    if (cleanCode) {
        return cleanCode;
    }

    if (Array.isArray(debt?.payments) && debt.payments.length > 0) {
        const foundPaymentWithTo = debt.payments.find(p => p.paymentCode && String(p.paymentCode).replace(/\s+/g, '').length >= 20);
        if (foundPaymentWithTo) {
            return String(foundPaymentWithTo.paymentCode).trim();
        }
    }

    return '';
};

const SendDebtsEmailModal = ({ show, onHide, company, debts = [], onSendEmail }) => {
    const [recipients, setRecipients] = useState('');
    const [subject, setSubject] = useState('');
    const [emailMessage, setEmailMessage] = useState('');
    const [presetSelection, setPresetSelection] = useState('CURRENT_MONTH');
    const [selectedInstallmentKeys, setSelectedInstallmentKeys] = useState(new Set());
    const [expandedDebts, setExpandedDebts] = useState({});
    const [isSending, setIsSending] = useState(false);
    const [saveAsDefault, setSaveAsDefault] = useState(false);

    const currentMonthLabel = useMemo(() => {
        const formatter = new Intl.DateTimeFormat('el-GR', { month: 'long', year: 'numeric' });
        const formatted = formatter.format(new Date());
        return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }, []);

    const dropdownOptions = [
        { value: 'CURRENT_MONTH', label: `Τρέχοντος Μήνα (${currentMonthLabel})` },
        { value: 'ALL', label: 'Όλες οι Απλήρωτες Δόσεις' }
    ];

    const isUnpaidInstallment = (inst) => {
        if (inst.remainingAmount !== undefined && inst.remainingAmount !== null) {
            return Number(inst.remainingAmount) > 0;
        }
        if (inst.totalAmount !== undefined && inst.totalAmount !== null) {
            return Number(inst.totalAmount) > 0;
        }
        return Number(inst.amount || 0) > 0;
    };

    const activeDebts = useMemo(() => {
        if (!Array.isArray(debts)) return [];
        return debts
            .filter((debt) => {
                const isDebtPaid = debt.status === 'PAID' || Number(debt.totalAmount || 0) <= 0;
                return !isDebtPaid;
            })
            .map((debt) => {
                const unpaidInsts = Array.isArray(debt.installments)
                    ? debt.installments.filter(isUnpaidInstallment)
                    : [];
                return {
                    ...debt,
                    installments: unpaidInsts
                };
            })
            .filter((debt) => debt.installments.length > 0);
    }, [debts]);

    useEffect(() => {
        if (!show) return;

        const newSelected = new Set();
        const initialExpanded = {};

        activeDebts.forEach((debt, dIdx) => {
            let hasSelectedChild = false;
            debt.installments.forEach((inst, iIdx) => {
                const key = `${debt._id || dIdx}_${inst._id || inst.installmentNo || iIdx}`;

                if (presetSelection === 'ALL') {
                    newSelected.add(key);
                    hasSelectedChild = true;
                } else if (presetSelection === 'CURRENT_MONTH' && isCurrentMonthInstallment(inst)) {
                    newSelected.add(key);
                    hasSelectedChild = true;
                }
            });
            initialExpanded[debt._id || dIdx] = hasSelectedChild;
        });

        setSelectedInstallmentKeys(newSelected);
        setExpandedDebts(initialExpanded);
    }, [presetSelection, activeDebts, show]);

    useEffect(() => {
        if (!show) return;

        const defaultSubject = `Ενημέρωση Οφειλών myAADE - ${company?.name || company?.companyName || ''} (Α.Φ.Μ.: ${company?.vatNumber || '-'})`;

        const applyConfig = (conf) => {
            if (conf && typeof conf === 'object' && Object.keys(conf).length > 0) {
                const emailsArray = Array.isArray(conf.emails) ? conf.emails : [conf.emails].filter(Boolean);
                const savedEmailsStr = emailsArray.join(', ');

                setRecipients(savedEmailsStr || company?.email || '');
                setSubject(conf.subject ? conf.subject : defaultSubject);
                setEmailMessage(conf.content || '');
                setPresetSelection(conf.dropdownOption || 'CURRENT_MONTH');
            } else {
                setRecipients(company?.email || '');
                setSubject(defaultSubject);
                setEmailMessage('');
                setPresetSelection('CURRENT_MONTH');
            }
        };

        const localUser = getCurrentUser();
        const userId = localUser?._id;

        if (userId) {
            axios.get(`${process.env.REACT_APP_API_URL2}/user/get-debts-email-preferences`, {
                params: { userId }
            })
                .then((res) => {
                    if (res.data?.status === '200') {
                        const serverConf = res.data.data;

                        applyConfig(serverConf);

                        if (localUser) {
                            if (serverConf) {
                                localUser.debtsEmailConf = serverConf;
                            } else {
                                delete localUser.debtsEmailConf;
                            }
                            localStorage.setItem('user', JSON.stringify(localUser));
                        }
                    } else {
                        applyConfig(null);
                    }
                })
                .catch(() => {
                    applyConfig(null);
                });
        } else {
            applyConfig(null);
        }

        setSaveAsDefault(false);
    }, [show, company?.vatNumber, company?._id, company?.id]);

    const isCurrentMonthInstallment = (inst) => {
        const dueDate = inst.dueDate;
        if (!dueDate) return false;
        const currentMonth = moment().format('YYYY-MM');
        return moment(dueDate, ['DD/MM/YYYY', 'YYYY-MM-DD']).format('YYYY-MM') === currentMonth;
    };

    const toggleExpandDebt = (debtId) => {
        setExpandedDebts((prev) => ({
            ...prev,
            [debtId]: !prev[debtId]
        }));
    };

    const handleCheckboxChange = (key) => {
        setSelectedInstallmentKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    };

    const handleSelectAllForDebt = (debt, dIdx) => {
        const debtId = debt._id || dIdx;
        const childKeys = debt.installments.map((inst, iIdx) => `${debtId}_${inst._id || inst.installmentNo || iIdx}`);
        const allChecked = childKeys.length > 0 && childKeys.every((k) => selectedInstallmentKeys.has(k));

        setSelectedInstallmentKeys((prev) => {
            const next = new Set(prev);
            childKeys.forEach((k) => {
                if (allChecked) next.delete(k);
                else next.add(k);
            });
            return next;
        });
    };

    const totalSelectedAmount = useMemo(() => {
        let sum = 0;
        activeDebts.forEach((debt, dIdx) => {
            debt.installments.forEach((inst, iIdx) => {
                const key = `${debt._id || dIdx}_${inst._id || inst.installmentNo || iIdx}`;
                if (selectedInstallmentKeys.has(key)) {
                    sum += Number(inst.remainingAmount ?? inst.totalAmount ?? 0);
                }
            });
        });
        return sum;
    }, [activeDebts, selectedInstallmentKeys]);

    const buildDebtsPdfBase64 = (payloadData) => {
        const doc = new jsPDF({
            orientation: 'portrait',
            unit: 'mm',
            format: 'a4'
        });

        doc.addFileToVFS('dejavusans.ttf', dejavuSans);
        doc.addFont('dejavusans.ttf', 'DejaVuSans', 'normal');
        doc.addFileToVFS('dejavusansBold.ttf', dejavuSansBold);
        doc.addFont('dejavusansBold.ttf', 'DejaVuSans', 'bold');

        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 14;
        let currentY = 15;

        const checkPageBreak = (neededHeight) => {
            if (currentY + neededHeight > pageHeight - 15) {
                doc.addPage();
                currentY = 15;
            }
        };

        doc.setFont('DejaVuSans', 'normal');
        doc.setFontSize(8.5);
        const infoDate = `Ημερομηνία Πληροφόρησης: ${moment().format('DD/MM/YYYY HH:mm:ss')}`;
        doc.text(infoDate, pageWidth - marginX, currentY, { align: 'right' });
        currentY += 7;

        doc.setFont('DejaVuSans', 'bold');
        doc.setFontSize(10.5);
        const compTitle = `ΟΦΕΙΛΕΣ ΠΡΟΣ ΤΟ ΔΗΜΟΣΙΟ - ${(company?.name || company?.companyName || '').toUpperCase()}`;
        doc.text(compTitle, marginX, currentY);
        currentY += 6;

        doc.setFontSize(10.5);
        doc.text(String(company?.vatNumber || payloadData[0]?.companyVatNumber || '').trim(), marginX, currentY);
        currentY += 8;

        doc.setFontSize(11);
        doc.text('Οφειλές Εκτός Ρύθμισης', marginX, currentY);
        currentY += 9;

        payloadData.forEach((debt) => {
            checkPageBreak(30);

            doc.setFont('DejaVuSans', 'bold');
            doc.setFontSize(9);
            doc.text(String(debt.taxType || 'ΟΦΕΙΛΗ').toUpperCase(), marginX, currentY);
            currentY += 5;

            const toCode = resolvePaymentIdentity(debt);
            if (toCode) {
                doc.setFont('DejaVuSans', 'normal');
                doc.setFontSize(8.5);
                doc.text(`Τ.Ο. ${toCode}`, marginX, currentY);
                currentY += 4.5;
            }

            doc.setFont('DejaVuSans', 'normal');
            doc.setFontSize(8);
            const initAmtStr = formatCurrency(debt.initialAmount || 0);
            const assessDateStr = debt.assessmentDate || '-';
            doc.text(`Αρχικό Βεβ. Ποσό:      ${initAmtStr}`, marginX, currentY);
            currentY += 4;
            doc.text(`Ημ/νία Βεβαίωσης:      ${assessDateStr}`, marginX, currentY);
            currentY += 5;

            const instRows = [];
            let sumInstRem = 0;
            let sumInstSur = 0;
            let sumInstTot = 0;

            (debt.selectedInstallments || []).forEach((inst) => {
                const rem = Number(inst.remainingAmount ?? inst.totalAmount ?? 0);
                const sur = Number(inst.surcharges || 0);
                const tot = Number(inst.totalAmount || (rem + sur));

                sumInstRem += rem;
                sumInstSur += sur;
                sumInstTot += tot;

                instRows.push([
                    { content: String(inst.installmentNo), styles: { halign: 'center' } },
                    { content: String(inst.dueDate || '-'), styles: { halign: 'center' } },
                    { content: formatCurrency(rem), styles: { halign: 'right' } },
                    { content: formatCurrency(sur), styles: { halign: 'right' } },
                    { content: formatCurrency(tot), styles: { halign: 'right' } }
                ]);
            });

            instRows.push([
                {
                    content: 'ΣΥΝΟΛΑ',
                    colSpan: 2,
                    styles: { fontStyle: 'bold', halign: 'left', fillColor: [248, 250, 252] }
                },
                { content: formatCurrency(sumInstRem), styles: { fontStyle: 'bold', halign: 'right', fillColor: [248, 250, 252] } },
                { content: formatCurrency(sumInstSur), styles: { fontStyle: 'bold', halign: 'right', fillColor: [248, 250, 252] } },
                { content: formatCurrency(sumInstTot), styles: { fontStyle: 'bold', halign: 'right', fillColor: [248, 250, 252] } }
            ]);

            autoTable(doc, {
                startY: currentY,
                margin: { left: marginX, right: marginX },
                head: [[
                    { content: 'Α/Α', styles: { halign: 'center' } },
                    { content: 'Ημ/νία Λήξης', styles: { halign: 'center' } },
                    { content: 'Υπόλ. Δόσης', styles: { halign: 'right' } },
                    { content: 'Προσαυξήσεις', styles: { halign: 'right' } },
                    { content: 'Συν. Υπόλοιπο', styles: { halign: 'right' } }
                ]],
                body: instRows,
                theme: 'grid',
                styles: {
                    font: 'DejaVuSans',
                    fontSize: 8,
                    cellPadding: { top: 2, right: 3, bottom: 2, left: 3 },
                    textColor: [30, 41, 59],
                    lineColor: [203, 213, 225],
                    lineWidth: 0.15
                },
                headStyles: {
                    font: 'DejaVuSans',
                    fontStyle: 'bold',
                    fontSize: 8,
                    fillColor: [241, 245, 249],
                    textColor: [15, 23, 42],
                    lineWidth: 0.2,
                    lineColor: [148, 163, 184]
                },
                columnStyles: {
                    0: { cellWidth: 16 },
                    1: { cellWidth: 36 },
                    2: { cellWidth: 42 },
                    3: { cellWidth: 42 },
                    4: { cellWidth: 46 }
                }
            });

            currentY = (doc.lastAutoTable ? doc.lastAutoTable.finalY : currentY) + 9;
        });

        const totalPages = doc.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFont('DejaVuSans', 'normal');
            doc.setFontSize(8);
            doc.text(`Σελ. ${i}`, pageWidth - marginX - 10, pageHeight - 8);
        }

        const dataUri = doc.output('datauristring');
        return dataUri.split(',')[1];
    };

    const handleSubmit = async () => {
        const emailList = recipients
            .split(',')
            .map((e) => e.trim())
            .filter((e) => e.length > 0);

        if (emailList.length === 0) {
            toast.error('Παρακαλώ συμπληρώστε τουλάχιστον ένα έγκυρο email παραλήπτη.');
            return;
        }

        if (selectedInstallmentKeys.size === 0) {
            toast.error('Δεν έχετε επιλέξει καμία απλήρωτη δόση προς αποστολή.');
            return;
        }

        const payloadData = activeDebts
            .map((debt, dIdx) => {
                const selectedInsts = debt.installments.filter((inst, iIdx) =>
                    selectedInstallmentKeys.has(`${debt._id || dIdx}_${inst._id || inst.installmentNo || iIdx}`)
                );
                return {
                    debtId: debt._id,
                    taxType: debt.taxType,
                    taxYear: debt.taxYear,
                    doy: debt.doy,
                    companyVatNumber: debt.companyVatNumber,
                    paymentCode: resolvePaymentIdentity(debt),
                    to: resolvePaymentIdentity(debt),
                    initialAmount: debt.initialAmount || debt.totalAmount || 0,
                    assessmentDate: debt.assessmentDate || '',
                    totalAmount: debt.totalAmount || 0,
                    selectedInstallments: selectedInsts
                };
            })
            .filter((d) => d.selectedInstallments.length > 0);

        try {
            setIsSending(true);

            const pdfBase64 = buildDebtsPdfBase64(payloadData);

            if (onSendEmail) {
                await onSendEmail({
                    emails: emailList,
                    subject: subject,
                    message: emailMessage,
                    presetSelection,
                    saveAsDefault,
                    debtsData: payloadData,
                    totalAmount: totalSelectedAmount,
                    pdfBase64: pdfBase64
                });
            }
            setIsSending(false);
            onHide();
        } catch (err) {
            setIsSending(false);
            toast.error(err?.message || 'Αποτυχία αποστολής email.');
        }
    };

    return (
        <Modal show={show} onHide={onHide} size="lg" dialogClassName="modal65PercentWidth" centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center">
                    Αποστολή Ενημέρωσης Οφειλών myAADE μέσω Email
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-3">
                <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold">
                        Emails Παραληπτών{' '}
                        <span className="text-muted font-weight-normal">
                            (χωρίστε με κόμμα αν είναι περισσότερα)
                        </span>
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="π.χ. info@company.gr, accounting@company.gr"
                        value={recipients}
                        onChange={(e) => setRecipients(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label className="font-weight-bold">
                        Θέμα Email
                    </Form.Label>
                    <Form.Control
                        type="text"
                        placeholder="Θέμα μηνύματος..."
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                    />
                </Form.Group>

                <Form.Group className="mb-4">
                    <Dropdown
                        label="Επιλογή Δόσεων"
                        options={dropdownOptions}
                        enabledValue={true}
                        value={dropdownOptions.find((opt) => opt.value === presetSelection)}
                        onChange={(e) => setPresetSelection(e.value)}
                    />
                </Form.Group>

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="font-weight-bold mb-0 text-dark">Εκκρεμείς Οφειλές & Απλήρωτες Δόσεις</h6>
                    <Badge
                        style={{
                            backgroundColor: THEME_COLOR,
                            color: '#fff',
                            fontSize: '0.85rem'
                        }}
                    >
                        Επιλεγμένο Σύνολο:{' '}
                        {totalSelectedAmount.toLocaleString('el-GR', { minimumFractionDigits: 2 })} €
                    </Badge>
                </div>

                {activeDebts.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                        Δεν υπάρχουν εκκρεμείς οφειλές με απλήρωτες δόσεις.
                    </div>
                ) : (
                    activeDebts.map((debt, dIdx) => {
                        const debtId = debt._id || dIdx;
                        const isExpanded = !!expandedDebts[debtId];
                        const childKeys = debt.installments.map(
                            (inst, iIdx) => `${debtId}_${inst._id || inst.installmentNo || iIdx}`
                        );
                        const checkedCount = childKeys.filter((k) =>
                            selectedInstallmentKeys.has(k)
                        ).length;
                        const isAllChecked = childKeys.length > 0 && checkedCount === childKeys.length;

                        return (
                            <div
                                key={debtId}
                                className="mb-2 rounded"
                                style={{ border: '1px solid #cbd5e1', backgroundColor: '#f8fafc' }}
                            >
                                <div
                                    className="d-flex align-items-center justify-content-between p-2 px-3"
                                    style={{ cursor: 'pointer', backgroundColor: '#f1f5f9' }}
                                    onClick={() => toggleExpandDebt(debtId)}
                                >
                                    <div className="d-flex align-items-center">
                                        <input
                                            type="checkbox"
                                            checked={isAllChecked}
                                            ref={(el) => {
                                                if (el) el.indeterminate = checkedCount > 0 && !isAllChecked;
                                            }}
                                            onClick={(e) => e.stopPropagation()}
                                            onChange={() => handleSelectAllForDebt(debt, dIdx)}
                                            style={getCheckboxStyle(isAllChecked, checkedCount > 0 && !isAllChecked)}
                                        />
                                        <span className="font-weight-bold text-dark ml-2 mr-1">
                                            {debt.taxType || 'Οφειλή'}
                                        </span>
                                        <span className="text-muted" style={{ fontSize: '0.85rem' }}>
                                            ({debt.taxYear ? `Έτος: ${debt.taxYear}` : ''} | ΔΟΥ: {debt.doy || '-'})
                                        </span>
                                    </div>
                                    <div className="d-flex align-items-center">
                                        <span className="font-weight-bold text-danger mr-3">
                                            {Number(debt.totalAmount || 0).toLocaleString('el-GR', {
                                                minimumFractionDigits: 2
                                            })}{' '}
                                            €
                                        </span>
                                        {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="p-2 bg-white">
                                        {debt.installments.map((inst, iIdx) => {
                                            const key = `${debtId}_${inst._id || inst.installmentNo || iIdx}`;
                                            const isChecked = selectedInstallmentKeys.has(key);
                                            const amount = Number(inst.remainingAmount ?? inst.totalAmount ?? 0);
                                            const dueDateStr = inst.dueDate || '-';
                                            const isCurrent = isCurrentMonthInstallment(inst);

                                            return (
                                                <div
                                                    key={key}
                                                    className="d-flex align-items-center justify-content-between py-1 px-3 border-bottom"
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <input
                                                            type="checkbox"
                                                            id={key}
                                                            checked={isChecked}
                                                            onChange={() => handleCheckboxChange(key)}
                                                            style={getCheckboxStyle(isChecked)}
                                                        />
                                                        <label
                                                            htmlFor={key}
                                                            className="mb-0 cursor-pointer d-flex align-items-center"
                                                        >
                                                            Δόση {inst.installmentNo || iIdx + 1}
                                                            <span className="text-muted ml-2">
                                                                (Λήξη: {dueDateStr})
                                                            </span>
                                                            {isCurrent && (
                                                                <Badge
                                                                    className="ml-2 text-white"
                                                                    style={{
                                                                        backgroundColor: THEME_COLOR,
                                                                        fontSize: '0.7rem'
                                                                    }}
                                                                >
                                                                    Τρέχων Μήνας
                                                                </Badge>
                                                            )}
                                                        </label>
                                                    </div>
                                                    <span className="font-weight-bold text-dark">
                                                        {amount.toLocaleString('el-GR', {
                                                            minimumFractionDigits: 2
                                                        })}{' '}
                                                        €
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}

                <Form.Group className="mb-2">
                    <Form.Label className="h6 font-weight-bold text-dark">
                        Μήνυμα / Σώμα Email
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={8}
                        placeholder="Γράψτε εδώ το συνοδευτικό κείμενο του μηνύματος..."
                        value={emailMessage}
                        onChange={(e) => setEmailMessage(e.target.value)}
                        style={{ fontSize: '0.875rem', lineHeight: '1.45' }}
                    />
                </Form.Group>

                <div className="d-flex align-items-center mb-2">
                    <input
                        type="checkbox"
                        id="saveEmailPreferences"
                        checked={saveAsDefault}
                        onChange={(e) => setSaveAsDefault(e.target.checked)}
                        style={getCheckboxStyle(saveAsDefault)}
                    />
                    <label htmlFor="saveEmailPreferences" className="mb-0 cursor-pointer text-dark font-weight-bold">
                        Αποθήκευση επιλογών
                    </label>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={handleSubmit}
                    disabled={isSending || activeDebts.length === 0}
                    style={{
                        backgroundColor: THEME_COLOR,
                        borderColor: THEME_COLOR,
                        color: '#fff'
                    }}
                >
                    {isSending && <Spinner animation="border" size="sm" className="mr-2" />}
                    Αποστολή Email
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default SendDebtsEmailModal;